import type { ReactNode } from "react";
import type { RouteT } from "@/route-types";
import type { TabKeyT } from "../tabs";
import type { ButtonVariant } from "@patient-watch/ui/button";
import { AnimatedIcon } from "@patient-watch/icons";

export type NavigationGroup = "care" | "quality" | "business" | "administration" | "tools" | "account";

export const navigationGroupLabels: Record<NavigationGroup, string> = {
    care: "Care",
    quality: "Quality",
    business: "Business",
    administration: "Administration",
    tools: "Tools",
    account: "Account",
};

export interface PageT {
    name: string;
    href: RouteT;
    description?: string;
    icon?: ReactNode;
    external?: boolean;
    draft?: boolean;
    iconOnly?: boolean;
    variant?: ButtonVariant;
    /** Tab key in `public.tabs`. Pages without one (Profile) are shown to everyone. */
    tab?: TabKeyT;
    /** Label names (e.g. "Elite Tendon Knee") - user must have at least one to see this page */
    labels?: string[];
    onClick?: () => void;
    group: NavigationGroup;
}

export const navigationContent: PageT[] = [
    {
        name: "Worklist",
        href: "/worklist" as RouteT,
        description: "Complete clinician forms and missing event details across your patients.",
        icon: <AnimatedIcon animation="clipboard" />,
        tab: "worklist",
        variant: "ghost",
        group: "care",
    },
    {
        name: "Patients",
        href: "/patients?tab=active" as RouteT,
        description: "Manage your patients all from one place.",
        icon: <AnimatedIcon animation="customer" />,
        tab: "patients",
        variant: "ghost",
        group: "care",
    },
    {
        name: "Diaries",
        href: "/diaries",
        description: "Create diaries for patients to track their symptoms.",
        icon: <AnimatedIcon animation="heart" />,
        variant: "ghost",
        tab: "diaries",
        group: "care",
    },
    {
        name: "Templates",
        href: "/templates",
        description: "Browse templates and invite patients to track outcomes.",
        icon: <AnimatedIcon animation="send" />,
        tab: "templates",
        variant: "ghost",
        group: "care",
    },
    {
        name: "Analytics",
        href: "/analytics",
        description: "Analyse the data from your patients",
        icon: <AnimatedIcon animation="arrow-bar-chart" />,
        tab: "analytics",
        variant: "ghost",
        group: "care",
    },
    {
        name: "Studies",
        href: "/studies",
        description: "Create studies and invite clinicians with label, form, and template access.",
        icon: <AnimatedIcon animation="notebook" />,
        tab: "studies",
        variant: "ghost",
        group: "quality",
    },
    {
        name: "Validate",
        href: "/validate" as RouteT,
        description: "Review and sign off the forms and schedules assigned to you.",
        icon: <AnimatedIcon animation="verified" />,
        tab: "validate",
        variant: "ghost",
        group: "quality",
    },
    {
        name: "Customers",
        href: "/customers/success",
        description: "Activation progress, ambassadors, churn risk, and anonymous submissions.",
        icon: <AnimatedIcon animation="society" />,
        tab: "customers",
        variant: "ghost",
        group: "business",
    },
    {
        name: "Sales",
        href: "/sales/leads",
        description: "Outreach tracker for leads, companies, touches, templates, and artefacts.",
        icon: <AnimatedIcon animation="social-media" />,
        tab: "sales",
        variant: "ghost",
        group: "business",
    },
    {
        name: "Admin",
        href: "/admin",
        description: "See what users are doing on Patient Watch.",
        icon: <AnimatedIcon animation="key" />,
        tab: "admin",
        variant: "ghost",
        group: "administration",
    },
    {
        name: "Users",
        href: "/users",
        description: "Browse users by role. Update organisations and billing from the table.",
        icon: <AnimatedIcon animation="add-user" />,
        tab: "users",
        variant: "ghost",
        group: "administration",
    },
    {
        name: "Builder",
        href: "/builder",
        description: "Control how users interact with Patient Watch",
        icon: <AnimatedIcon animation="helmet" />,
        tab: "builder",
        variant: "ghost",
        group: "tools",
    },
    {
        name: "Design",
        href: "/design",
        description: "Component gallery and product-flow previews.",
        icon: <AnimatedIcon animation="view" />,
        tab: "design",
        variant: "ghost",
        group: "tools",
    },
    {
        name: "Developer",
        href: "/developer",
        description: "Operations, report delivery, and debugging tools.",
        icon: <AnimatedIcon animation="hammer" />,
        tab: "developer",
        variant: "ghost",
        group: "tools",
    },
    {
        name: "Profile",
        href: "/profile",
        description: "Update the information required for Patient Watch to work.",
        icon: <AnimatedIcon animation="profile" />,
        variant: "ghost",
        group: "account",
    },
    {
        name: "Team",
        href: "/team",
        variant: "ghost",
        description: "Everyone in your organisations. Invite colleagues and choose a default contact person.",
        icon: <AnimatedIcon animation="social-media" />,
        tab: "team",
        group: "account",
    },
];
